import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardList,
  RefreshCw,
  Phone,
  User,
  MessageSquare,
  Clock,
  ArrowRight,
  Facebook,
  Instagram,
  Music2,
  MessageCircle,
  Copy,
  Check,
} from 'lucide-react';
import PageHeader from './ui/PageHeader';
import EmptyState from './ui/EmptyState';
import apiService from '../services/apiService';
import {
  INTENT_LABELS,
  PLATFORM_META,
  formatPhoneDisplay,
  inheritSummaryPhone,
  groupSummariesByConversation,
  platformMeta,
  stripPhonesFromText,
  telHref,
} from '../utils/conversationSummaries';

const SENTIMENT_STYLES = {
  positive: { label: 'Olumlu', className: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30' },
  neutral: { label: 'Nötr', className: 'bg-slate-500/10 text-slate-300 ring-slate-500/30' },
  negative: { label: 'Olumsuz', className: 'bg-rose-500/10 text-rose-300 ring-rose-500/30' },
};

const PLATFORM_FILTERS = [
  { id: 'all', label: 'Tümü' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'voice', label: 'Telefon' },
];

function Badge({ className = '', children }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}
    >
      {children}
    </span>
  );
}

function PlatformIcon({ platform, className = 'h-3.5 w-3.5' }) {
  const id = platformMeta(platform).id;
  if (id === 'instagram') return <Instagram className={className} />;
  if (id === 'tiktok') return <Music2 className={className} />;
  if (id === 'whatsapp') return <MessageCircle className={className} />;
  if (id === 'voice') return <Phone className={className} />;
  return <Facebook className={className} />;
}

function PlatformBadge({ platform }) {
  const meta = platformMeta(platform);
  return (
    <Badge className={meta.className}>
      <PlatformIcon platform={platform} />
      {meta.label}
    </Badge>
  );
}

function leadScoreStyle(score) {
  if (score == null) return 'bg-slate-500/10 text-slate-300 ring-slate-500/30';
  if (score >= 70) return 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30';
  if (score >= 40) return 'bg-amber-500/10 text-amber-300 ring-amber-500/30';
  return 'bg-slate-500/10 text-slate-300 ring-slate-500/30';
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PhoneCallout({ phone, caption }) {
  const [copied, setCopied] = useState(false);
  if (!phone) return null;
  const display = formatPhoneDisplay(phone);
  const href = telHref(phone);

  async function copyNumber(event) {
    event.preventDefault();
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(display.replace(/\s/g, ''));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-emerald-400/10 px-4 py-3 ring-1 ring-emerald-400/35">
      <div className="min-w-0">
        {caption ? (
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-300/80">{caption}</p>
        ) : null}
        <a
          href={href}
          className="mt-0.5 block font-mono text-2xl font-semibold tracking-wide text-emerald-50 hover:text-white sm:text-[1.7rem]"
        >
          {display}
        </a>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={copyNumber}
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/5 px-3 py-2 text-xs font-medium text-emerald-100 ring-1 ring-emerald-400/20 hover:bg-white/10"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Kopyalandı' : 'Kopyala'}
        </button>
        <a
          href={href}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-400/20 px-3 py-2 text-xs font-semibold text-emerald-50 ring-1 ring-emerald-300/30 hover:bg-emerald-400/30"
        >
          <Phone className="h-3.5 w-3.5" />
          Ara
        </a>
      </div>
    </div>
  );
}

function SummaryCard({ summary, history, expanded, onToggle }) {
  const sentiment = SENTIMENT_STYLES[summary.sentiment] || null;
  const displayName = summary.customerName || summary.externalUserId;
  const phone = inheritSummaryPhone(summary, history);
  const nextActionText = summary.nextAction
    ? phone
      ? stripPhonesFromText(summary.nextAction)
      : summary.nextAction
    : '';

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <User className="h-4 w-4 shrink-0 text-slate-400" />
            <h3 className="truncate text-base font-semibold text-white">{displayName}</h3>
            <PlatformBadge platform={summary.platform} />
            <Badge className="bg-white/[0.06] text-slate-300 ring-white/10">
              v{summary.version}
            </Badge>
            {summary.resolved === true && (
              <Badge className="bg-emerald-500/10 text-emerald-300 ring-emerald-500/30">
                Tamamlandı
              </Badge>
            )}
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDateTime(summary.windowEnd || summary.createdAt)}
            </span>
            {summary.messageCount != null && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {summary.messageCount} mesaj
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {summary.leadScore != null && (
            <Badge className={leadScoreStyle(summary.leadScore)}>
              Lead {summary.leadScore}
            </Badge>
          )}
          {summary.intent && (
            <Badge className="bg-teal-500/10 text-teal-300 ring-teal-500/30">
              {INTENT_LABELS[summary.intent] || summary.intent}
            </Badge>
          )}
          {sentiment && <Badge className={sentiment.className}>{sentiment.label}</Badge>}
        </div>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-slate-200">{summary.summary}</p>

      {(nextActionText || phone) && (
        <div className="mt-3 rounded-xl bg-white/[0.03] px-3 py-3 ring-1 ring-white/10">
          {nextActionText ? (
            <p className="flex items-start gap-2 text-sm text-slate-300">
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />
              {nextActionText}
            </p>
          ) : null}
          {phone ? (
            <div className={nextActionText ? 'mt-3' : ''}>
              <PhoneCallout
                phone={phone}
                caption={nextActionText ? 'İletişim numarası' : 'Müşteri telefonu'}
              />
            </div>
          ) : null}
        </div>
      )}

      {summary.topics?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {summary.topics.map((topic) => (
            <Badge key={topic} className="bg-white/[0.04] text-slate-400 ring-white/10">
              {topic}
            </Badge>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 border-t border-white/[0.07] pt-3">
          <button
            type="button"
            onClick={onToggle}
            className="text-xs font-medium text-teal-400 hover:text-teal-300"
          >
            {expanded
              ? 'Önceki özetleri gizle'
              : `Önceki ${history.length} özeti göster`}
          </button>
          {expanded && (
            <div className="mt-3 space-y-3">
              {history.map((old) => (
                <div key={old.id} className="rounded-lg bg-white/[0.02] px-3 py-2">
                  <p className="text-xs text-slate-500">
                    v{old.version} · {formatDateTime(old.createdAt)}
                  </p>
                  <p className="mt-1 text-sm text-slate-400">{old.summary}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConversationSummaries() {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [platformFilter, setPlatformFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setError(null);
      setSummaries(await apiService.fetchConversationSummaries());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return apiService.addUpdateListener((event) => {
      if (event.type === 'conversation_summary') load();
    });
  }, [load]);

  const grouped = useMemo(() => {
    const filtered =
      platformFilter === 'all'
        ? summaries
        : summaries.filter((s) => platformMeta(s.platform).id === platformFilter);
    return groupSummariesByConversation(filtered);
  }, [summaries, platformFilter]);

  const counts = useMemo(() => {
    const latest = groupSummariesByConversation(summaries);
    const byPlatform = { all: latest.length, facebook: 0, instagram: 0, tiktok: 0, whatsapp: 0, voice: 0 };
    latest.forEach(({ latest: row }) => {
      const id = platformMeta(row.platform).id;
      if (byPlatform[id] != null) byPlatform[id] += 1;
    });
    return byPlatform;
  }, [summaries]);

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        title="Görüşme Özetleri"
        description="Kanal görüşmeleri sessiz kaldığında AI tarafından özetlenir (Facebook, Instagram, TikTok, WhatsApp, telefon)."
        actions={
          <button type="button" onClick={load} className="btn-secondary flex items-center gap-2 text-sm">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </button>
        }
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {PLATFORM_FILTERS.map((filter) => {
          const active = platformFilter === filter.id;
          const meta = filter.id === 'all' ? null : PLATFORM_META[filter.id];
          return (
            <button
              key={filter.id}
              type="button"
              onClick={() => setPlatformFilter(filter.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ring-1 transition-colors ${
                active
                  ? 'bg-emerald-400/15 text-emerald-200 ring-emerald-400/30'
                  : 'bg-white/[0.03] text-slate-300 ring-white/10 hover:bg-white/[0.06]'
              }`}
            >
              {meta && <PlatformIcon platform={filter.id} />}
              {filter.label}
              <span className="text-slate-500">({counts[filter.id] ?? 0})</span>
            </button>
          );
        })}
      </div>

      {error && (
        <p className="mb-6 rounded-lg bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</p>
      )}

      {loading ? (
        <p className="text-slate-400">Yükleniyor…</p>
      ) : grouped.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title="Henüz özet yok"
          description="Bu kanalda henüz özet yok. Messenger görüşmesi 5 dakika sessiz kaldığında burada görünür."
        />
      ) : (
        <div className="space-y-4">
          {grouped.map(({ key, latest, history }) => (
            <SummaryCard
              key={key}
              summary={latest}
              history={history}
              expanded={Boolean(expanded[key])}
              onToggle={() =>
                setExpanded((prev) => ({
                  ...prev,
                  [key]: !prev[key],
                }))
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
